import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { loadingTrue, loadingFalse } from "../../../Reducer/loaderSlice";
import Handler from "../../../Helpers/Handler";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";
import "../../../Assets/css/style.css";

const STATUS_META = {
  NEW: { icon: "bx-shield", color: "#346dbd", bg: "#f1f5f9", label: "Not Requested" },
  PENDING: { icon: "bx-time-five", color: "#d97706", bg: "#fffbeb", label: "Pending Review" },
  APPROVED: { icon: "bxs-badge-check", color: "#16a34a", bg: "#f0fdf4", label: "Verified" },
  REJECTED: { icon: "bx-x-circle", color: "#dc2626", bg: "#fef2f2", label: "Rejected" },
};

const VerificationRequest = () => {
  const dispatch = useDispatch();

  const [profile, setProfile] = useState(null);
  const [gst, setGst] = useState("");
  const [license, setLicense] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  /* Fetch current profile/verification status */
  const fetchProfile = async () => {
    dispatch(loadingTrue());
    const res = await Handler({ method: "GET", url: "/users/profile.php" });
    dispatch(loadingFalse());
    if (res?.success) {
      const bp = res.data?.business_profile;
      setProfile(bp);
      setGst(bp?.gst_number || "");
      setLicense(bp?.license_number || "");
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  const validate = () => {
    const e = {};
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gst.trim()) e.gst = "GST number is required";
    else if (!gstRegex.test(gst.toUpperCase())) e.gst = "Invalid GST format (e.g. 33ABCDE1234F1Z5)";
    if (!license.trim()) e.license = "License number is required";
    else if (license.trim().length < 5) e.license = "License number too short";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const res = await Handler({
      method: "POST",
      url: "/verified_batch/verify_request.php",
      data: { gst_number: gst.toUpperCase().trim(), license_number: license.trim() },
    });
    setSubmitting(false);
    if (res?.success) {
      toast.success("Verification request submitted!");
      fetchProfile();
    } else {
      toast.error(res?.message || "Failed to submit");
    }
  };

  const status = profile?.verification_status || "NEW";
  const meta = STATUS_META[status] || STATUS_META.NEW;
  const isVerified = profile?.is_verified === 1 || profile?.is_verified === "1";
  const isPending = status === "PENDING";
  const isRejected = status === "REJECTED";
  const canSubmit = !isVerified && !isPending;

  return (
    <MainLayout>
      <FeedLayout>
        <div className="vr-page">

          {/* Page Header */}
          <div className="vr-header">
            <h1 className="vr-title">
              Business Verification
            </h1>
            <p className="vr-subtitle">
              Get your business verified to build trust with clients and vendors
            </p>
          </div>

          {/* Status Banner */}
          {profile && (
            <div className="vr-status-banner" style={{ background: meta.bg, borderColor: meta.color + "40" }}>
              <div className="vr-status-left">
                <div className="vr-status-icon" style={{ color: meta.color }}>
                  <i className={`bx ${meta.icon}`} />
                </div>
                <div>
                  <div className="vr-status-label" style={{ color: meta.color }}>
                    {meta.label}
                  </div>
                  {status === "PENDING" && (
                    <div className="vr-status-desc">
                      Your request is under review. We'll notify you once processed.
                    </div>
                  )}
                  {status === "APPROVED" && (
                    <div className="vr-status-desc">
                      Your business is verified! A badge is shown on your profile.
                    </div>
                  )}
                  {status === "REJECTED" && profile?.verification_reason && (
                    <div className="vr-status-desc vr-status-desc--danger">
                      Reason: {profile.verification_reason}
                    </div>
                  )}
                </div>
              </div>
              {isVerified && (
                <div className="vr-verified-seal">
                  <i className="bx bxs-badge-check" /> Verified
                </div>
              )}
            </div>
          )}

          {/* Benefits */}
          <div className="vr-benefits">
            <div className="vr-benefit">
              <i className="bx bxs-badge-check vr-benefit-icon" style={{ color: "#2563eb" }} />
              <div>
                <div className="vr-benefit-title">Verified Badge</div>
                <div className="vr-benefit-desc">Stand out with a verified checkmark on your profile</div>
              </div>
            </div>
            <div className="vr-benefit">
              <i className="bx bxs-star vr-benefit-icon" style={{ color: "#f59e0b" }} />
              <div>
                <div className="vr-benefit-title">Top Rankings</div>
                <div className="vr-benefit-desc">Appear higher in search and vendor rankings</div>
              </div>
            </div>
            <div className="vr-benefit">
              <i className="bx bx-shield vr-benefit-icon" style={{ color: "#16a34a" }} />
              <div>
                <div className="vr-benefit-title">Trusted Business</div>
                <div className="vr-benefit-desc">Clients prefer verified businesses for deals & events</div>
              </div>
            </div>
          </div>

          {/* Form */}
          {canSubmit && (
            <div className="vr-form-card">
              <h2 className="vr-form-title">
                <i className="bx bx-file" />
                {isRejected ? "Resubmit Verification" : "Submit Verification Request"}
              </h2>
              <p className="vr-form-desc">
                Provide your GST number and business license to get verified.
              </p>

              <div className="vr-form">
                {/* GST */}
                <div className="vr-field">
                  <label className="vr-label">
                    GST Number <span className="vr-required">*</span>
                  </label>
                  <input
                    className={`vr-input ${errors.gst ? "vr-input--error" : ""}`}
                    placeholder="e.g. 33ABCDE1234F1Z5"
                    value={gst}
                    maxLength={15}
                    onChange={(e) => {
                      setGst(e.target.value.toUpperCase());
                      setErrors((p) => ({ ...p, gst: "" }));
                    }}
                  />
                  {errors.gst && (
                    <span className="vr-error">
                      <i className="bx bx-error-circle" /> {errors.gst}
                    </span>
                  )}
                  <span className="vr-hint">
                    15-character GST Identification Number
                  </span>
                </div>

                {/* License */}
                <div className="vr-field">
                  <label className="vr-label">
                    License Number <span className="vr-required">*</span>
                  </label>
                  <input
                    className={`vr-input ${errors.license ? "vr-input--error" : ""}`}
                    placeholder="e.g. LIC-2026-001"
                    value={license}
                    onChange={(e) => {
                      setLicense(e.target.value);
                      setErrors((p) => ({ ...p, license: "" }));
                    }}
                  />
                  {errors.license && (
                    <span className="vr-error">
                      <i className="bx bx-error-circle" /> {errors.license}
                    </span>
                  )}
                  <span className="vr-hint">
                    Your business license or registration number
                  </span>
                </div>

                <button
                  className="vr-btn-submit"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <><i className="bx bx-loader-alt bx-spin" /> Submitting…</>
                  ) : (
                    <><i className="bx bx-send" /> {isRejected ? "Resubmit Request" : "Submit for Verification"}</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Pending state — no form, just info */}
          {isPending && (
            <div className="vr-form-card vr-form-card--pending">
              <i className="bx bx-time-five vr-pending-icon" />
              <h3>Under Review</h3>
              <p>
                Your verification request has been submitted and is currently being
                reviewed by our admin team. This usually takes 1–2 business days.
              </p>
              <div className="vr-pending-details">
                <div className="vr-pending-row">
                  <span>GST Number</span>
                  <strong>{profile?.gst_number || "—"}</strong>
                </div>
                <div className="vr-pending-row">
                  <span>License Number</span>
                  <strong>{profile?.license_number || "—"}</strong>
                </div>
              </div>
            </div>
          )}

          {/* Approved state */}
          {isVerified && (
            <div className="vr-form-card vr-form-card--approved">
              <i className="bx bxs-badge-check vr-approved-icon" />
              <h3>Business Verified!</h3>
              <p>
                Your business has been successfully verified. Your verified badge
                is now visible on your profile.
              </p>
              <div className="vr-pending-details">
                <div className="vr-pending-row">
                  <span>GST Number</span>
                  <strong>{profile?.gst_number || "—"}</strong>
                </div>
                <div className="vr-pending-row">
                  <span>License Number</span>
                  <strong>{profile?.license_number || "—"}</strong>
                </div>
                {profile?.verified_at && (
                  <div className="vr-pending-row">
                    <span>Verified On</span>
                    <strong>
                      {new Date(profile.verified_at).toLocaleDateString("en-IN", {
                        day: "2-digit", month: "long", year: "numeric"
                      })}
                    </strong>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </FeedLayout>
    </MainLayout>
  );
};

export default VerificationRequest;